/*NOTE: f1118-schd-c component is common for CDE schedules, making changes here will affect all the CDE schedules. 
Incase adhoc feature is required for E or any change of requirement in C/D, consider spinning a new component for the schd*/
import { Component, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CurrencyPipe } from '@angular/common';
import { Subscription } from 'rxjs';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
} from '../../../shared/workspace-element-context';
import { F1118SchdCDEService } from '../f1118-schd-c.service';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
const SCHD_TYPE_PANEL_MAP: Record<string, string> = {
  'F1118_C': 'Schedule C Details',
  'F1118_DPI': 'Schedule D Part I Details',
  'F1118_DPII': 'Schedule D Part II Details',
  'F1118_EPI': 'Schedule E Part I Details',
  'F1118_EPII': 'Schedule E Part II Details',
};

const KNOWN_CALLBACKS = new Set([
  'addNewCustomOverride',
  'addNewCustomOverrideSchdDPI',
  'addNewCustomOverrideSchdDPII',
  'addNewCustomOverrideSchdEPI',
  'addNewCustomOverrideSchdEPII',
  'F1118-SCH-C',
]);

const CALLBACK_SCHD_TYPE_MAP: Record<string, string> = {
  'F1118-SCH-C': 'F1118_C',
  'addNewCustomOverride': 'F1118_C',
  'addNewCustomOverrideSchdDPI': 'F1118_DPI',
  'addNewCustomOverrideSchdDPII': 'F1118_DPII',
  'addNewCustomOverrideSchdEPI': 'F1118_EPI',
  'addNewCustomOverrideSchdEPII': 'F1118_EPII',
};

interface LineColDetail {
  COL_LINE_DESC: string;
  COL_DESC?: string;
  IS_AMT: string;
  ELEMENT_VALUE: string | number;
  IS_READ_ONLY: boolean;
  IS_UNIQUE_ID_COL?: string;
  IS_TOTAL_LINE?: string;
  SCHD_KEY?: any;
  LINE_KEY?: any;
  COL_KEY?: any;
  COL_LONG_DESC?: string;
  IS_DEFAULT?: any;
  COMMENT?: string;
  ELEMENT_NAME_1?: any;
  LINE_CODE_SHORT_DESC?: string;
}

@Component({
    selector: 'gtw-f1118-schd-c',
    templateUrl: './f1118-schd-c.component.html',
    styleUrls: ['./f1118-schd-c.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [CurrencyPipe],
    standalone: false
})
export class F1118SchdCComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  private readonly subs = new Subscription();

  baseGlobalParams: any;
  baseURLs: any = {'admin':'','api':'/gtw','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  
  private comboKey: number = 0;
  private baseURL = '/custom';
  private baseURLGtw = '/gtw';
  private taxYear: number = 0;
  private scenario: number = 0;
  private jcdKey: number = 0;
  private screenKey: number = 0;
  private ssoId: string = '';
  private scenarioCode: string = '';
  private checkLockedObj: any = [];
  private rowData: any;
  private schdType: string = '';
  private isInitialized: boolean = false;
  private filterParams: any = {};
  private originalLineColDtlValues: (string | number)[] = [];
  private originalSelectedBasketId: any = 0;

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private el: ElementRef,
    private f1118SchdCService: F1118SchdCDEService,
    private currencyPipe: CurrencyPipe,
  ) { }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const fp = normalizeFilterParams(activeScreen);
    const gp = this.gtwGlobal.global?.globalParams || {};

    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.filterParams = fp;

    this.baseGlobalParams = {
      ...gp,
      scenarioCode: gp.scenarioCode || '',
      is_issue_key_enabled: gp.is_issue_key_enabled || '',
      issue_id: gp.issue_id || '',
      issue_short_desc: gp.issue_short_desc || '',
    };

    this.taxYear = Number(fp.tax_year || gp.tax_year) || 0;
    this.scenario = Number(fp.scenario || gp.scenario) || 0;
    this.jcdKey = Number(gp.jcd_key) || 0;
    this.screenKey = Number(activeScreen?.screen_key || gp.screen_key) || 0;
    this.scenarioCode = gp.scenarioCode || '';
    this.ssoId = this.gtwApp.USER_SETTINGS?.user?.sso_id || '';

    this.comboKey = Number(fp.filer_entity_key || gp.filer_entity_key || 0) || 0;

    const ceHost = this.el.nativeElement?.closest?.('gtw-f1118-schd-c-ce') || this.el.nativeElement;

    const modalName = activeScreen?.data?.modalName
      || activeScreen?.data?.modal_name
      || ceHost?.getAttribute?.('modal-name')
      || '';

    if (modalName && !KNOWN_CALLBACKS.has(modalName)) {
      console.warn(`[F1118SchdC] Unknown modalName "${modalName}" — not found in KNOWN_CALLBACKS.`);
    }

    // Replicate old AngularJS logic: gridData.length == 0 ? {} : gridData.rowData
    // When a batch/toolbar action fires with no row selected, gridData is [].
    // When a row-level click fires, gridData is an object { colData, rowData }.
    const gridData = activeScreen?.data?.gridData;
    const isAddMode = Array.isArray(gridData) && gridData.length === 0;

    let row: any = {};
    if (!isAddMode) {
      // Read directly from activeScreen.data.rowData (set by CeHostDialogComponent)
      // instead of rowPayloadFromActiveScreen which prioritises stale selected.rowData
      row = activeScreen?.data?.rowData || {};
      if (typeof row === 'string') {
        try { row = safeJsonInput<any>(row, null, 'f1118-schd-c.parse1'); } catch { row = {}; }
      }
      if (Array.isArray(row)) { row = row[0] || {}; }
      if (typeof row !== 'object' || row === null) { row = {}; }

      if (!row.object_id) {
        const domRowData = ceHost?.getAttribute?.('row-data');
        if (domRowData) {
          try { row = safeJsonInput<any>(domRowData, null, 'f1118-schd-c.parse2'); } catch { /* keep current */ }
        }
      }
    }

    this.rowData = row;

    this.checkLockedObj = [{ rowData: this.rowData, scenario_key: this.scenario }];

    const domSchdType = ceHost?.getAttribute?.('schd-type') || '';
    const domPanelHeader = ceHost?.getAttribute?.('panel-header') || '';

    this.schdType = activeScreen?.data?.schdType
      || activeScreen?.data?.schd_type
      || CALLBACK_SCHD_TYPE_MAP[modalName]
      || domSchdType
      || 'F1118_C';

    this.panelHeader = activeScreen?.data?.panelHeader
      || activeScreen?.data?.panel_header
      || domPanelHeader
      || SCHD_TYPE_PANEL_MAP[this.schdType]
      || 'Schedule C Details';
  }

  minimise = false;

  issueIdList: any[] = [];
  
  SavedData: any = [];
  lineColDtls: LineColDetail[] = [];
  basketList: any = [];
  selectedBasketId: any = 0;
  fieldsPerRow: any = 2;
  noOfFields: any = 40;
  tempGroupData: any = {};
  tableData = false;
  isCustomOverrideWindow: boolean = false;
  isEdit: boolean = false;
  isDeltaEdit: boolean = false;
  isViewOnly: boolean = false;
  processedTableData: any = [];
  editedSno: any = 0;
  isDataChanged: boolean = false;
  isTableLoading: boolean = false;
  isModalLoading = false;
  panelHeader: string = '';

  checkInputType(): boolean {
    if (Number.isInteger(parseInt(this.taxYear + '')) &&
      Number.isInteger(parseInt(this.scenario + '')) &&
      Number.isInteger(parseInt(this.jcdKey + '')) &&
      Number.isInteger(parseInt(this.screenKey + '')) &&
      Number.isInteger(parseInt(this.comboKey + '')) &&
      Number.isInteger(parseInt(this.ssoId + ''))
      && (this.rowData && this.rowData != undefined)
    ) {
      return true;
    }
    return false;
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.checkInputType()) return;
    this.isInitialized = true;
    this.init();
  }

  init(): void {
    this.getModalData();
  }

  onChangeEvent(event: any) {
    console.log(event);
  }

  onClickButtonEvent(event: any) {
    console.log(event);
  }

  public floatingConfig = {
    isFloating: true
  };

  getTestData() {
  }

  getModalData(): void {
    if (this.isModalLoading) return;

    this.isModalLoading = true;
    this.basketList = [];
    this.isEdit = false;
    this.isCustomOverrideWindow = false;
    this.cdr.markForCheck();

    if (this.rowData && Object.keys(this.rowData).length > 0 && this.rowData.object_id > 0) {
      this.isEdit = true;
      this.isCustomOverrideWindow = this.rowData.SOURCE == 'Custom Override';
    } else {
      this.isCustomOverrideWindow = true;
    }

    this.f1118SchdCService.getBasketList(this.baseURLs.api, this.taxYear, this.scenario, this.jcdKey, this.ssoId).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          if (data && data.length > 0) {
            data.forEach((value: any) => {
              let dropDownFromatData = {
                value: value.BASKET_KEY,
                label: value.BASKET_DESC,
                selected: false
              };
              this.basketList.push(dropDownFromatData);
            });
          } else {
            console.log('Problem loading basket dtls');
          }
          this.cdr.markForCheck();
        },
        (error: any) => {
          console.log(error);
          this.cdr.markForCheck();
        },
        () => {
          this.cdr.markForCheck();
        }
      );

    this.f1118SchdCService.getLineColDtlsData(this.baseURLs.api, this.baseGlobalParams,
      this.taxYear, this.scenario, this.jcdKey, this.comboKey, this.schdType, this.ssoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (data: any) => {
          if (data && data.length > 0) {
            this.tableData = false;
            this.SavedData = [];
            this.lineColDtls = this.processLineColDtls(data);
            if (this.isEdit) {
              this.getDataByUniqueKey();
            }
          } else {
            this.tableData = false;
            this.SavedData = [];
          }
          this.cdr.markForCheck();
        },
        (error: any) => {
          this.isModalLoading = false;
          console.log(error);
          this.cdr.markForCheck();
        },
        () => {
          if (!this.isEdit) {
            this.isModalLoading = false;
          }
          this.cdr.markForCheck();
        }
      );
  }

  getDataByUniqueKey(): void {
    this.isTableLoading = true;
    this.cdr.markForCheck();
    this.f1118SchdCService.getDataByUniqueKey(this.baseURLs.api, this.baseGlobalParams,
      this.taxYear, this.scenario, this.jcdKey, this.comboKey, this.ssoId, this.schdType, this.rowData.ELEMENT_NAME_1).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (data: any) => {
          this.isTableLoading = false;
          if (data && data.length > 0) {
            this.setFieldData(data, this.lineColDtls, this.isCustomOverrideWindow);
            this.setBasketData(data);
            this.saveFormSnapshot();
            let processTableData = this.processData(data);
            this.processedTableData = processTableData;
          } else {
            this.processedTableData = [];
          }
          this.cdr.markForCheck();
        },
        (error: any) => {
          this.isModalLoading = false;
          this.isTableLoading = false;
          console.log(error);
          this.cdr.markForCheck();
        },
        () => {
          this.isModalLoading = false;
          this.isTableLoading = false;
          this.cdr.markForCheck();
        }
      );
  }

  setBasketData(source: any): void {
    this.selectedBasketId = source[0]?.BASKET_KEY || 0;
  }

  setFieldData(source: any, dest: any, isGetAllFields: boolean): void {
    let matchingObj: any = {};
    for (var i = 0; i < dest.length; i++) {
      if (dest[i].IS_READ_ONLY || isGetAllFields) {
        matchingObj = source.find((item: any) => {
          return item.COL_LONG_DESC == dest[i].COL_LONG_DESC && item.LINE_CODE_SHORT_DESC == dest[i].LINE_CODE_SHORT_DESC;
        });
        if (matchingObj && matchingObj.ELEMENT_VALUE) {
          dest[i].ELEMENT_VALUE = matchingObj.ELEMENT_VALUE;
        }
      }
    }
  }

  onBasketChange(basketKey: any): void {
    this.selectedBasketId = basketKey;
  }

  processLineColDtls(data: any): any[] {
    for (var i = 0; i < data.length; i++) {
      data[i].IS_READ_ONLY = false;

      if (data[i].IS_TOTAL_LINE == '1') {
        data.splice(i, 1);
        i--;
      }
    }
    return data;
  }

  viewICalcData(item: any): void {
    this.isViewOnly = true;
    this.resetElementValueOfLineColDtls();
    this.setFieldData(item.items, this.lineColDtls, true);
    this.cdr.markForCheck();
  }

  editICalcDelta(item: any): void {
    this.isViewOnly = false;
    this.editedSno = item.SNO;
    this.resetElementValueOfLineColDtls();
    this.isDeltaEdit = true;
    this.setFieldData(item.items, this.lineColDtls, true);
    this.saveFormSnapshot();
    this.cdr.markForCheck();
  }

  processData(formLines: any): any[] {
    let tableRowTemplate = safeJsonInput<any>(JSON.stringify(this.lineColDtls), null, 'f1118-schd-c.parse3');
    for (var i = 0; i < tableRowTemplate.length; i++) {
      tableRowTemplate[i].ELEMENT_VALUE = '';
      tableRowTemplate[i].BASKET_KEY = this.selectedBasketId;
    }

    return _.reduce(
      formLines,
      function (memo: any, property: any) {
        let bucket: any = _.find(memo, {
          SCHD_KEY: property.SCHD_KEY,
          SNO: property.SNO
        });

        if (!bucket) {
          bucket = {
            SCHD_KEY: property.SCHD_KEY,
            SNO: property.SNO,
            items: safeJsonInput<any>(JSON.stringify(tableRowTemplate), null, 'f1118-schd-c.parse4')
          };
          memo.push(bucket);
        }

        let matchingCellObj: any = bucket.items.find((item: any) => {
          return item.COL_LONG_DESC == property.COL_LONG_DESC && item.LINE_CODE_SHORT_DESC == property.LINE_CODE_SHORT_DESC;
        });

        if (matchingCellObj) {
          matchingCellObj.ELEMENT_VALUE = property.ELEMENT_VALUE;
        }

        return memo;
      },
      []
    );
  }

  validateForm(lineColDtls: any): boolean {
    const errors: string[] = [];
    let einRefIdCount = 0;
    let isEinRefIdColumnExists = false;

    if (this.selectedBasketId == undefined || this.selectedBasketId == 0) {
      errors.push('Please select Basket');
    }

    for (var i = 0; i < lineColDtls.length; i++) {
      if (lineColDtls[i].COL_LONG_DESC == 'FGN_CORP_EIN_REF_ID') {
        isEinRefIdColumnExists = true;
        if (lineColDtls[i].ELEMENT_VALUE && lineColDtls[i].ELEMENT_VALUE.trim().length > 0) {
          einRefIdCount++;
        }
      }
    }

    if (isEinRefIdColumnExists == true) {
      if (einRefIdCount == 0) {
        errors.push('EIN or Ref ID is required');
      } else if (einRefIdCount > 1) {
        errors.push('Enter only either EIN or Ref ID');
      }
    }

    if (errors.length > 0) {
      this.f1118SchdCService.openSnackBar(errors.join(' | '), 'gtw-snackbar--red');
      return true;
    }
    return false;
  }

  saveData(): void {
    this.isDataChanged = true;

    if (!this.validateForm(this.lineColDtls)) {
      let operationType = '';
      if (this.isCustomOverrideWindow && this.isEdit) {
        operationType = 'CUST_OVERRIDE_UPDATE';
      } else if (this.isCustomOverrideWindow) {
        operationType = 'CUST_OVERRIDE_NEW';
      } else if (this.isEdit) {
        if (this.isDeltaEdit) {
          operationType = 'IC_DELTA_UPDATE';
        } else {
          operationType = 'IC_DELTA_NEW';
        }
      } else {
        console.log('Is some flag not set????');
      }

      let unquieId = this.isCustomOverrideWindow ? this.composeUnquieId(this.lineColDtls) : this.rowData.ELEMENT_NAME_1;
      let originalUniqueId = this.isEdit ? this.rowData.ELEMENT_NAME_1 : unquieId;
      let originalBasketKey = this.isEdit ? this.rowData.BASKET_KEY : this.selectedBasketId;
      let dataToSave = this.composeDataToSave(this.lineColDtls, unquieId, operationType);

      let clobSettings: any = {
        SSO_ID: this.ssoId,
        TAX_YEAR: this.taxYear,
        SCENARIO: this.scenario,
        JCD_KEY: this.jcdKey,
        FILER_ENTITY_KEY: this.comboKey,
        ORIGINAL_BASKET_KEY: originalBasketKey,
        ORIGINAL_UNIQ_KEY: originalUniqueId,
        OPERATION_TYPE: operationType
      };

      this.resetAll();

      this.f1118SchdCService.saveF1118SchdCDEData(this.baseURLs.api, dataToSave, clobSettings, this.baseGlobalParams, this.checkLockedObj).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (data: any) => {
          if (data.callSuccess == '1') {
            this.f1118SchdCService.openSnackBar('Data Saved Successfully', 'gtw-snackbar--green');
            if (this.isCustomOverrideWindow) {
              this.requestGridUpdate.emit('refresh');
            }
            this.getDataByUniqueKey();
          } else {
            this.f1118SchdCService.openSnackBar('Error Saving Data. ' + data.errorMessage, 'gtw-snackbar--red');
          }
          this.cdr.markForCheck();
        },
        (error: any) => {
          console.log('Error while saving');
          this.cdr.markForCheck();
        }
      );
    }
    this.cdr.markForCheck();
  }

  composeDataToSave(lineColDtls: any[], unquieId: any, operationType: any): any[] {
    let dataToSave: any = [];
    let maxSno = 0;
    for (var i = 0; i < this.processedTableData.length; i++) {
      if (this.processedTableData[i].SNO > maxSno) {
        maxSno = this.processedTableData[i].SNO;
      }
    }

    let sno = operationType == 'IC_DELTA_UPDATE' ? this.editedSno : (maxSno + 1);

    for (var i = 0; i < this.lineColDtls.length; i++) {
      if (!this.isCustomOverrideWindow &&
        this.lineColDtls[i].IS_AMT == '1' && this.lineColDtls[i].IS_UNIQUE_ID_COL == '1') {
        continue;
      }
      dataToSave[i] = {};
      dataToSave[i].SCHD_KEY = this.lineColDtls[i].SCHD_KEY;
      dataToSave[i].LINE_KEY = this.lineColDtls[i].LINE_KEY;
      dataToSave[i].COL_KEY = this.lineColDtls[i].COL_KEY;
      dataToSave[i].BASKET_KEY = this.selectedBasketId;
      dataToSave[i].COL_LONG_DESC = this.lineColDtls[i].COL_LONG_DESC;
      dataToSave[i].IS_DEFAULT = this.lineColDtls[i].IS_DEFAULT;
      dataToSave[i].ELEMENT_VALUE = this.lineColDtls[i].ELEMENT_VALUE;
      dataToSave[i].COMMENT = this.lineColDtls[i].COMMENT;
      dataToSave[i].ELEMENT_NAME_1 = unquieId;
      dataToSave[i].SNO = sno;
    }
    this.doNotSaveNonDefaultLineIfEmpty(dataToSave);
    this.saveEinOrRefId(dataToSave);
    return dataToSave;
  }

  doNotSaveNonDefaultLineIfEmpty(dataToSave: any): void {
    let indexOfEmptyField = -1;
    for (var i = 0; i < dataToSave.length; i++) {
      if (dataToSave[i]?.IS_DEFAULT == 'N') {
        if (dataToSave[i].ELEMENT_VALUE == undefined) {
          indexOfEmptyField = i;
        } else if (dataToSave[i].ELEMENT_VALUE.trim().length == 0) {
          indexOfEmptyField = i;
        }
        if (indexOfEmptyField > -1) {
          dataToSave.splice(indexOfEmptyField, 1);
          indexOfEmptyField = -1;
          i--;
          continue;
        }
      }
    }
  }

  saveEinOrRefId(dataToSave: any): void {
    let indexOfEmptyField = -1;
    for (var i = 0; i < dataToSave.length; i++) {
      if (dataToSave[i]?.COL_LONG_DESC.includes('FGN_CORP_EIN_REF_ID')) {
        if (dataToSave[i].ELEMENT_VALUE == undefined) {
          indexOfEmptyField = i;
        } else if (dataToSave[i].ELEMENT_VALUE.trim().length == 0) {
          indexOfEmptyField = i;
        }
        if (indexOfEmptyField > -1) {
          dataToSave.splice(indexOfEmptyField, 1);
          indexOfEmptyField = -1;
          i--;
          continue;
        }
      }
    }
  }

  deleteData(deleteSno: any): void {
    this.isDataChanged = true;
    let operationType = '';
    let isCustomOverrideDelete = this.isEdit && this.isCustomOverrideWindow;
    if (isCustomOverrideDelete) {
      operationType = 'CUST_OVERRIDE_DELETE';
    } else {
      operationType = 'ICALC_DELTA_DELETE';
    }
    let originalUniqueId = this.rowData.ELEMENT_NAME_1;
    let originalBasketKey = this.rowData.BASKET_KEY;

    let clobSettings: any = {
      SSO_ID: this.ssoId,
      TAX_YEAR: this.taxYear,
      SCENARIO: this.scenario,
      JCD_KEY: this.jcdKey,
      FILER_ENTITY_KEY: this.comboKey,
      ORIGINAL_BASKET_KEY: originalBasketKey,
      ORIGINAL_UNIQ_KEY: originalUniqueId,
      OPERATION_TYPE: operationType,
      DELETE_SNO: deleteSno
    };

    let dataToSave: any = [];
    this.f1118SchdCService.saveF1118SchdCDEData(this.baseURLs.api, dataToSave, clobSettings, this.baseGlobalParams, this.checkLockedObj).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess == '1') {
          this.f1118SchdCService.openSnackBar('Data Deleted Successfully', 'gtw-snackbar--green');
          if (this.isCustomOverrideWindow) {
            this.modalClose();
          } else {
            this.getDataByUniqueKey();
          }
        } else {
          this.f1118SchdCService.openSnackBar('Error Saving Data. ' + data.errorMessage, 'gtw-snackbar--red');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.log('Error while saving');
        this.cdr.markForCheck();
      }
    );
  }

  resetElementValueOfLineColDtls(): void {
    for (var i = 0; i < this.lineColDtls.length; i++) {
      if (this.lineColDtls[i].IS_UNIQUE_ID_COL != '1') {
        this.lineColDtls[i].ELEMENT_VALUE = '';
      }
    }
  }

  private saveFormSnapshot(): void {
    this.originalLineColDtlValues = this.lineColDtls.map(item => item.ELEMENT_VALUE);
    this.originalSelectedBasketId = this.selectedBasketId;
  }

  private restoreFormSnapshot(): void {
    for (let i = 0; i < this.lineColDtls.length && i < this.originalLineColDtlValues.length; i++) {
      this.lineColDtls[i].ELEMENT_VALUE = this.originalLineColDtlValues[i];
    }
    this.selectedBasketId = this.originalSelectedBasketId;
  }

  onResetClick(): void {
    if (this.isDeltaEdit || (this.isEdit && this.isCustomOverrideWindow)) {
      this.restoreFormSnapshot();
    } else {
      for (let i = 0; i < this.lineColDtls.length; i++) {
        this.lineColDtls[i].ELEMENT_VALUE = '';
      }
      this.selectedBasketId = 0;
    }
    this.isViewOnly = false;
    this.cdr.markForCheck();
  }

  resetAll(): void {
    this.resetElementValueOfLineColDtls();
    this.isDeltaEdit = false;
    this.isViewOnly = false;
    this.cdr.markForCheck();
  }

  composeUnquieId(dataToSave: any): string {
    let uniqueId = '';
    let uniqueIdCount = 0;
    for (var i = 0; i < this.lineColDtls.length; i++) {
      if (this.lineColDtls[i].IS_UNIQUE_ID_COL == '1' && this.lineColDtls[i].ELEMENT_VALUE != undefined
        && this.lineColDtls[i].COL_LONG_DESC != 'FGN_CORP_NAME') {
        if (uniqueIdCount == 0) {
          uniqueId = String(this.lineColDtls[i].ELEMENT_VALUE);
        } else {
          uniqueId = uniqueId + '_' + String(this.lineColDtls[i].ELEMENT_VALUE);
        }
        uniqueIdCount++;
      }
    }
    if (uniqueIdCount == 0) {
      uniqueId = new Date().getTime() + '';
    }
    return uniqueId;
  }

  setUniqueId(dataToSave: any, unquieId: any): void {
    for (var i = 0; i < this.lineColDtls.length; i++) {
      this.lineColDtls[i].ELEMENT_NAME_1 = unquieId;
    }
  }

  modalClose(): void {
    this.requestGridUpdate.emit(this.isDataChanged ? 'refresh' : 'cancel');
  }

  setAriaLabel(item: any, contextLabel: string = 'Option'): string {
    const label = item?.label || item?.text || '-';
    return `${contextLabel} option ${label}`.trim();
  }
}
